import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { emitEvent } from '@/lib/coreEvents';
import { logTimelineEvent } from '@/lib/coreTimeline';
import { consumeCoreEvent } from '@/lib/coreEventConsumers';
import { reindexOne } from '@/lib/coreSearch';
import { invalidateForEvent } from '@/lib/coreCache';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Câblage automatique des événements (spec 39 + 31)
// Une seule fonction permet à n'importe quel module (commande, facture,
// réservation, client…) d'émettre un événement du socle ET d'enrichir la
// Timeline centrale, sans jamais bloquer le métier (best-effort).
// ─────────────────────────────────────────────────────────────

export interface CoreActivityInput {
  /** Id du marchand (users.id / idvendeur). Sert à retrouver l'organisation Core. */
  commerceId: number | null | undefined;
  /** Clé d'événement du socle, ex : order.created, invoice.paid. */
  eventKey: string;
  /** Type d'entité reliée : order, invoice, booking, appointment, customer… */
  entityType: string;
  /** Identifiant de l'entité reliée. */
  entityId: string | number;
  /** Titre lisible inscrit dans la Timeline. */
  title: string;
  description?: string | null;
  amount?: number | null;
  currency?: string | null;
  payload?: Record<string, unknown> | null;
  actorUserId?: number | null;
  actorName?: string | null;
  source?: string | null;
}

/** Associe une clé d'événement à un type d'étape de la Timeline. */
export function eventKeyToTimelineType(eventKey: string | null | undefined): string {
  const key = (eventKey || '').toLowerCase();
  if (key.startsWith('order.')) return 'order';
  if (key.startsWith('invoice.')) return key.includes('paid') ? 'payment' : 'invoice';
  if (key.startsWith('payment.') || key.includes('.paid')) return 'payment';
  if (key.startsWith('booking.')) return 'booking';
  if (key.startsWith('appointment.')) return 'appointment';
  if (key.startsWith('quote.')) return 'quote';
  if (key.startsWith('customer.created') || key.endsWith('.created')) return 'created';
  if (key.includes('.updated') || key.includes('.status')) return 'status';
  return 'custom';
}

/**
 * Émet un événement du socle + journalise l'activité dans la Timeline.
 * Ne lève jamais d'erreur : un échec du socle ne doit pas casser le métier.
 */
export async function emitCoreActivity(input: CoreActivityInput): Promise<void> {
  try {
    if (!input.commerceId) return;
    const org = await getOrganizationByCommerce(input.commerceId);
    if (!org) return;

    await Promise.allSettled([
      emitEvent({
        tenantId: org.tenant_id,
        organizationId: org.id,
        eventKey: input.eventKey,
        entityType: input.entityType,
        entityId: input.entityId,
        payload: input.payload ?? null,
        actorUserId: input.actorUserId ?? null,
        source: input.source || 'module',
      }),
      logTimelineEvent({
        tenantId: org.tenant_id,
        organizationId: org.id,
        entityType: input.entityType,
        entityId: input.entityId,
        eventType: eventKeyToTimelineType(input.eventKey),
        title: input.title,
        description: input.description ?? null,
        amount: input.amount ?? null,
        currency: input.currency ?? null,
        actorUserId: input.actorUserId ?? null,
        actorName: input.actorName ?? null,
        refTable: input.entityType,
        refId: String(input.entityId),
        metadata: input.payload ?? null,
      }),
    ]);

    // Consommateurs de l'événement : workflows + notifications (best-effort).
    try {
      await consumeCoreEvent({
        tenantId: org.tenant_id,
        organizationId: org.id,
        eventKey: input.eventKey,
        entityType: input.entityType,
        entityId: input.entityId,
        payload: input.payload ?? null,
        // Le consentement de l'acteur filtre les canaux sortants (spec 56/57).
        recipientUserId: input.actorUserId ?? null,
      });
    } catch {
      /* les consommateurs ne doivent jamais bloquer le métier */
    }

    // Réindexation automatique de l'entité concernée (spec 43).
    try {
      await reindexOne({
        tenantId: org.tenant_id,
        organizationId: org.id,
        commerceId: input.commerceId,
        entityType: input.entityType,
        entityId: input.entityId,
      });
    } catch {
      /* l'index ne doit jamais bloquer le métier */
    }

    // Invalidation automatique du cache (spec 65) : les modules lourds
    // (catalogue, rapports, dashboard) ne servent jamais une donnée périmée
    // après une écriture métier. Best-effort.
    try {
      await invalidateForEvent(org.id, input.eventKey);
    } catch {
      /* l'invalidation du cache ne doit jamais bloquer le métier */
    }
  } catch {
    /* best-effort : le câblage automatique ne doit jamais bloquer le métier */
  }
}